import { motion } from "framer-motion";
import { Sparkle } from "@phosphor-icons/react";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { TYPES, usd } from "./booking-shared";

/* ---------- Step: Property type & size ---------- */
export function StepPropertyType({ cfg, o, r, pickType, toggleOversized, sqft, acres, editSqft, editAcres }) {
  const overPrice = cfg.oversized?.price || 197;
  const inclSqft = Math.round(cfg.oversized?.auto_sqft || 5000);
  const inclAcre = Math.round(cfg.oversized?.auto_acre || 50);
  return (
    <div data-testid="step-property">
      <h2 className="font-display text-2xl sm:text-3xl text-[#4A0000] tracking-tight mb-1">Tell us about the property</h2>
      <p className="text-sm text-[#5C5A58] mb-6">This fine-tunes your pricing — size, acreage & property type.</p>

      {/* Property type */}
      <div>
        <Label className="text-xs uppercase tracking-[0.14em] text-[#8A6D1B] font-semibold">Property type</Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2" data-testid="type-grid">
          {TYPES.map((t) => {
            const on = o.property_type === t.id;
            return (
              <motion.button key={t.id} data-testid={`type-${t.id}`} onClick={() => pickType(t)}
                whileHover={{ y: -2 }} whileTap={{ scale: 0.985 }} transition={{ type: "spring", stiffness: 340, damping: 24 }}
                className={`flex items-start gap-2 rounded-xl border p-3 text-left min-w-0 whitespace-normal transition-colors ${on ? "border-[#B08D57] bg-gradient-to-b from-[#FBF6EA] to-[#F6EEDB]" : "border-[#E9DDC6] bg-white hover:border-[#D9CFB0]"}`}>
                <t.icon size={18} weight="duotone" className={`shrink-0 ${on ? "text-[#4A0000]" : "text-[#8A8782]"}`} />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-[#1A1715] leading-tight break-words">{t.t}</span>
                  <span className="block text-[11px] text-[#8A8782] leading-tight mt-0.5 break-words">{t.d}</span>
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Size */}
      <div className="mt-6">
        <Label className="text-xs text-[#5C5A58]">Property size</Label>
        <p className="text-[11px] text-[#8A6D1B] mt-1 leading-snug">
          <Sparkle size={11} weight="fill" className="text-[#B08D57] inline align-[-1px] mr-1" />
          Every shoot includes properties up to <b className="font-semibold text-[#4A0000]">{inclSqft.toLocaleString()} sq&nbsp;ft</b> and/or <b className="font-semibold text-[#4A0000]">{inclAcre} acres</b>.
        </p>
        <div className="grid grid-cols-2 gap-3 mt-1.5">
          <div>
            <Input data-testid="size-sqft" type="number" value={sqft ?? ""} placeholder="Square feet" onChange={(e) => editSqft(e.target.value)} className="border-[#E9DDC6]" />
          </div>
          <div>
            <Input data-testid="size-acres" type="number" step="0.1" value={acres ?? ""} placeholder="Acres" onChange={(e) => editAcres(e.target.value)} className="border-[#E9DDC6]" />
          </div>
        </div>
        {/* Oversized: auto-detected with manual override */}
        <button data-testid="oversized-toggle" onClick={toggleOversized}
          className={`mt-2 w-full rounded-lg border px-3 py-2.5 text-left text-sm whitespace-normal break-words transition ${r.oversized ? "border-[#B08D57] bg-[#B08D57]/10" : "border-[#E9DDC6] bg-white hover:border-[#D9CFB0]"}`}>
          <span className="flex items-center justify-between gap-3">
            <span className="min-w-0 flex-1 font-medium text-[#1A1715]">
              {r.oversized ? "✓ Oversized property" : "Oversized property?"}
              <span className="block text-[11px] text-[#8A8782] font-normal break-words">
                {o.oversized === null ? "Auto-detected from size" : o.oversized ? "Added manually" : "Turned off"} · One flat fee, unlimited sqft/acreage
              </span>
            </span>
            <span className="font-mono text-sm text-[#4A0000] shrink-0">+{usd(overPrice)}</span>
          </span>
        </button>
        <p className="text-[11px] text-[#8A8782] mt-1.5">
          On the cusp?{" "}
          <button type="button" data-testid="contact-team-link"
            onClick={() => {
              const el = document.querySelector('[data-testid="quick-book"]');
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                el.classList.add("rb-flash");
                setTimeout(() => el.classList.remove("rb-flash"), 1600);
              }
            }}
            className="text-[#7E1119] font-semibold underline underline-offset-2 hover:text-[#4A0000] transition-colors">
            Contact the Reelty team
          </button>{" "}before your shoot.
        </p>
      </div>
    </div>
  );
}
